import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { getApifacilPairingCode, pauseApifacilInstance, requestApifacilPairingCode } from "../../api/client";

const PAIRING_TTL_MS = 120_000;

export default function WhatsappQrPairingSection({ channelId, hasSession }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (!channelId || !hasSession) return null;

  const remaining = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0;

  const run = async (fn, ok) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const data = await fn();
      const next = data?.pairingCode || data?.data?.pairingCode || data?.data?.codigo || "";
      if (next) {
        setCode(String(next));
        setExpiresAt(Date.now() + PAIRING_TTL_MS);
      }
      setNotice(ok);
    } catch (err) {
      setError(err?.message || "Não foi possível concluir.");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setNotice("Código copiado.");
    } catch {
      setError("Não foi possível copiar o código.");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Conectar com código
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Gere um código e digite no celular: Configurações → Aparelhos conectados → Conectar com código. O código vale cerca de 2 minutos.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          label="Telefone com DDI"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="5511999999999"
        />
        <Button
          variant="outlined"
          disabled={busy || !phone}
          onClick={() => run(() => requestApifacilPairingCode(channelId, phone), "Código gerado. Ele vale por cerca de 2 minutos.")}
        >
          {busy ? <CircularProgress size={16} /> : "Gerar código"}
        </Button>
        <Button
          variant="text"
          disabled={busy}
          onClick={() => run(() => getApifacilPairingCode(channelId), "Código atual consultado.")}
        >
          Ver código atual
        </Button>
        <Button
          variant="text"
          color="warning"
          disabled={busy}
          onClick={() =>
            run(() => pauseApifacilInstance(channelId), "Sessão pausada. A conexão foi mantida; inicie de novo pelo QR ou código para voltar a enviar.")
          }
        >
          Pausar sessão
        </Button>
      </Stack>
      {code ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <Typography variant="h6" sx={{ letterSpacing: 2 }}>
            {code}
          </Typography>
          <Button size="small" onClick={copyCode}>
            Copiar
          </Button>
          <Typography variant="caption" color="text.secondary">
            {remaining > 0 ? `Expira em ${remaining}s` : "Expirado — gere outro código"}
          </Typography>
        </Stack>
      ) : null}
      {notice ? <Alert severity="success" sx={{ mt: 1 }}>{notice}</Alert> : null}
      {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}
    </Box>
  );
}
